
import * as React from 'react';
import { FormattedHTMLMessage, injectIntl } from 'react-intl';
import { produce } from 'immer';
import {
    VerificationService,
    StudentPersonalInfoViewModel,
    FieldValidationErrors,
    ExtendedFieldValidationErrors,
    StudentPersonalInfoResponse,
    hasFailedInstantMatch,
    Metadata,
    Organization,
    updateViewModelOrganization,
    updateFieldValidationErrorsByFieldId,
    handleEmailOnKeyDown,
    submitForm,
    VerificationStepsEnum,
    isFormFilled,
    setRef,
    CollegeNameComponent,
    FirstNameComponent,
    LastNameComponent,
    EmailComponent,
    BirthDateComponent,
    FormFooterComponent,
    MarketConsentWrapper,
} from '@sheerid/jslib';
import { LogoComponent } from '../LogoComponent/LogoComponent'
import { isRenderedInIframe } from '../IsRenderedInIframe';

interface StepStudentPersonalInfoComponentProps {
    intl: any;
    verificationService: VerificationService;
}

const StepStudentPersonalInfo = ({ intl, verificationService }: StepStudentPersonalInfoComponentProps) => {
    const viewModel = verificationService.viewModel as StudentPersonalInfoViewModel;
    const fieldValidationErrors: FieldValidationErrors & ExtendedFieldValidationErrors = verificationService.fieldValidationErrors;
    const verificationResponse = verificationService.verificationResponse as StudentPersonalInfoResponse;
    const failedInstantMatch = hasFailedInstantMatch(verificationResponse);
    const renderedInIframe = isRenderedInIframe();

    const updateStudentViewModel = (key: keyof StudentPersonalInfoViewModel, value: string | Metadata) => {
        const nextState: StudentPersonalInfoViewModel = produce(viewModel, (draft: StudentPersonalInfoViewModel) => {
            // @ts-ignore
            draft[key] = value;
        });
        verificationService.updateViewModel(nextState);
    };

    return (
        <div id="sid-step-student-personal-info" className={`${!renderedInIframe ? 'sid-l-container' : ''}`}>
            <div className="sid-header">
                <div className={`sid-logo sid-l-horz-center ${renderedInIframe ? 'sid-lightbox__logo' : ''}`}>
                    <LogoComponent verificationService={verificationService} />
                </div>
                <div className={`sid-header__title ${renderedInIframe ? 'sid-lightbox__title' : ''}`}>
                    <FormattedHTMLMessage id="step.personalInfo.title" defaultMessage="Verify Your Details" />
                </div>
            </div>
            <div className={`sid-l-container ${renderedInIframe ? 'sid-lightbox' : ''}`}>
                { failedInstantMatch ?
                    <div className="sid-header">

                        <div className="sid-header__subtitle sid-header__subtitle--error">
                            <FormattedHTMLMessage id="step.personalInfo.tryAgain.subtitle"
                                defaultMessage="Your full name and birthdate must match what is on your official school records." />
                        </div>
                    </div>
                :
                    <div className="sid-header">

                        <div className="sid-header__subtitle--bold">
                                <FormattedHTMLMessage id="step.personalInfo.subtitle"
                                    defaultMessage="Complete the form below to confirm your eligibility." />
                            &nbsp;
                        </div>
                    </div>
                }

                <CollegeNameComponent
                    value={(verificationService.viewModel as StudentPersonalInfoViewModel).organization}
                    verificationService={verificationService}
                    isErrored={!!fieldValidationErrors.organization}
                    onChange={
                        (choice: Organization) => {
                            updateViewModelOrganization(choice, verificationService);
                            updateFieldValidationErrorsByFieldId('organization', choice, verificationService);
                        }
                    }
                />

                <div className="sid-names">
                    <FirstNameComponent
                        value={viewModel.firstName}
                        isErrored={!!fieldValidationErrors.firstName}
                        onChange={
                            (newValue: string) => {
                                updateStudentViewModel('firstName', newValue);
                                updateFieldValidationErrorsByFieldId('firstName', newValue, verificationService);
                            }
                        }
                    />

                    <LastNameComponent
                        value={viewModel.lastName}
                        isErrored={!!fieldValidationErrors.lastName}
                        onChange={
                            (newValue: string) => {
                                updateStudentViewModel('lastName', newValue);
                                updateFieldValidationErrorsByFieldId('lastName', newValue, verificationService);
                            }
                        }
                    />
                </div>

                <EmailComponent
                    value={viewModel.email}
                    isErrored={!!fieldValidationErrors.email}
                    onChange={
                        (newValue: string) => {
                            updateStudentViewModel('email', newValue);
                            updateFieldValidationErrorsByFieldId('email', newValue, verificationService);
                        }
                    }
                    onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => handleEmailOnKeyDown(event)}
                />

                <BirthDateComponent
                    isErrored={!!fieldValidationErrors.birthDate}
                    onChange={(newValue: string) => {
                        updateStudentViewModel('birthDate', newValue);
                        updateFieldValidationErrorsByFieldId('birthDate', newValue, verificationService);
                    }}
                    value={viewModel.birthDate}
                />

                <MarketConsentWrapper
                    verificationService={verificationService}
                    isErrored={!!fieldValidationErrors.marketConsentValue}
                    onChange={(newValue) => {
                        updateStudentViewModel('metadata', { ...verificationService.viewModel.metadata, marketConsentValue: newValue });
                        updateFieldValidationErrorsByFieldId('marketConsentValue', newValue, verificationService);
                    }}
                    viewModel={viewModel}
                />

                <div className="sid-form-region sid-submit-wrapper sid-l-space-top-md">
                    <div className="sid-submit">
                        <button
                            id="sid-submit-wrapper__collect-info"
                            onClick={() => {
                                if (verificationService.viewModel.metadata.marketConsentValue) {
                                    updateStudentViewModel('metadata', { 'opt-in': 1 });
                                }
                                submitForm(viewModel, verificationService, VerificationStepsEnum.collectStudentPersonalInfo);
                            }}
                            type="submit"
                            className={`sid-btn sid-btn--dark sid-l-full-width ${!isFormFilled(viewModel, verificationService.formValidationOptions) ? 'sid-btn--disabled-like' : ''}`}
                            aria-label="submit"
                            ref={(button: HTMLButtonElement) => setRef('submitButton', button)}
                        >
                            { failedInstantMatch ?
                                <FormattedHTMLMessage id="tryAgain" defaultMessage="Try Again" />
                            :
                                <FormattedHTMLMessage id="verifyMyStudentStatus" defaultMessage="Verify My Student Status" />
                            }
                        </button>
                    </div>
                </div>


                <FormFooterComponent verificationService={verificationService}>
                    <FormattedHTMLMessage
                        id="studentInfoShared"
                        defaultMessage={`Information entered into this web form will be used for verification purposes. The information will be shared with L.L.Bean. To view L.L.Bean's Privacy Policy <a href="https://www.llbean.com/customerService/privacy/index.html"  target="_blank" className="sid-link">click here</a>.<br><br>`}
                    />
                </FormFooterComponent>
            </div>
        </div>
    );
};

export const StepStudentPersonalInfoComponent = injectIntl(StepStudentPersonalInfo);
